import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, Mail } from "lucide-react";
import { api, formatApiErrorDetail, setSeo } from "@/lib/api";
import { markEmailSignupComplete } from "@/components/site/EmailSignup";

// One page serving the three links that appear in our emails: confirm, preferences, unsubscribe.
const MODES = {
  "/email/confirm": "confirm",
  "/email/preferences": "preferences",
  "/email/unsubscribe": "unsubscribe",
};

const Shell = ({ title, children }) => (
  <div className="px-5 md:px-8 py-20 md:py-28 bg-bone">
    <div className="max-w-[640px] mx-auto bg-white border border-line px-7 md:px-12 py-12">
      <span className="eyebrow">Human Home Living</span>
      <h1 className="display text-[34px] md:text-[42px] leading-[1.1] text-charcoal mt-4 mb-6">
        {title}
      </h1>
      {children}
      <div className="mt-10 pt-6 border-t border-line">
        <Link
          to="/"
          data-testid="email-page-home-link"
          className="text-[12.5px] tracking-[0.14em] uppercase text-sage hover:text-sage-dark transition-colors"
        >
          Return to Human Home Living
        </Link>
      </div>
    </div>
  </div>
);

export default function EmailPreferences() {
  const { pathname, search } = useLocation();
  const mode = MODES[pathname] || "preferences";
  const token = new URLSearchParams(search).get("token") || "";

  const [state, setState] = useState({ loading: true, error: "", profile: null, done: false });
  const [tags, setTags] = useState([]);
  const [busy, setBusy] = useState(false);
  const ran = useRef(false);
  // Tracks the real mounted state. StrictMode runs mount -> unmount -> mount, so this ends true,
  // whereas a per-effect `cancelled` flag would be stuck true for the only request that fires.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    setSeo({
      title: "Email preferences | Human Home Living",
      description: "Manage your Human Home Living email subscription.",
      noindex: true,
    });
  }, []);

  useEffect(() => {
    // StrictMode mounts effects twice in development; the confirm call must fire once.
    if (ran.current) return undefined;
    ran.current = true;
    const run = async () => {
      if (!token) {
        setState({ loading: false, error: "This link is missing its code.", profile: null, done: false });
        return;
      }
      try {
        if (mode === "confirm") {
          const { data } = await api.post("/email/confirm", { token });
          markEmailSignupComplete();
          if (mounted.current)
            setState({ loading: false, error: "", profile: { email: data.email }, done: true });
          return;
        }
        const { data } = await api.get("/email/profile", { params: { token } });
        if (mounted.current) {
          setTags(data.tags || []);
          setState({ loading: false, error: "", profile: data, done: false });
        }
      } catch (err) {
        if (mounted.current)
          setState({
            loading: false,
            error: formatApiErrorDetail(err.response?.data?.detail),
            profile: null,
            done: false,
          });
      }
    };
    run();
    return undefined;
  }, [mode, token]);

  const savePreferences = async () => {
    setBusy(true);
    try {
      await api.post("/email/preferences", { token, tags });
      setState((s) => ({ ...s, done: true }));
    } catch (err) {
      setState((s) => ({ ...s, error: formatApiErrorDetail(err.response?.data?.detail) }));
    } finally {
      setBusy(false);
    }
  };

  const confirmUnsubscribe = async () => {
    setBusy(true);
    try {
      await api.post("/email/unsubscribe", { token });
      setState((s) => ({ ...s, done: true, profile: { ...s.profile, status: "unsubscribed" } }));
    } catch (err) {
      setState((s) => ({ ...s, error: formatApiErrorDetail(err.response?.data?.detail) }));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading)
    return (
      <Shell title="One moment…">
        <p className="text-[15px] font-light text-[#6A665F]">Checking your link.</p>
      </Shell>
    );

  if (state.error)
    return (
      <Shell title="That link didn't work">
        <p data-testid="email-page-error" className="text-[15px] font-light text-[#6A665F]">
          {state.error}
        </p>
      </Shell>
    );

  if (mode === "confirm")
    return (
      <Shell title="You're subscribed.">
        <div data-testid="email-confirmed">
          <Check className="text-sage" size={28} />
          <p className="text-[16px] font-light leading-relaxed text-[#5A5A5A] mt-4">
            Thank you for confirming <strong className="font-normal">{state.profile?.email}</strong>.
            You'll receive occasional buying guides, product discoveries and home living
            inspiration. Every email carries an unsubscribe link.
          </p>
        </div>
      </Shell>
    );

  if (mode === "unsubscribe")
    return (
      <Shell title={state.done ? "You've been unsubscribed." : "Unsubscribe"}>
        {state.done ? (
          <p data-testid="email-unsubscribed" className="text-[16px] font-light text-[#5A5A5A]">
            {state.profile?.email} has been removed from our mailing list. We won't email you
            again unless you subscribe once more yourself.
          </p>
        ) : (
          <>
            <p className="text-[15.5px] font-light leading-relaxed text-[#6A665F] mb-7">
              Confirm that you no longer wish to receive emails at{" "}
              <strong className="font-normal text-charcoal">{state.profile?.email}</strong>.
            </p>
            <button
              onClick={confirmUnsubscribe}
              disabled={busy}
              data-testid="email-unsubscribe-confirm"
              className="px-7 py-3.5 bg-charcoal text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-sage transition-colors disabled:opacity-60"
            >
              {busy ? "Working…" : "Unsubscribe me"}
            </button>
          </>
        )}
      </Shell>
    );

  return (
    <Shell title="Your email preferences">
      <p className="text-[15.5px] font-light leading-relaxed text-[#6A665F] mb-7">
        <Mail size={15} className="inline mr-2 text-sage" />
        {state.profile?.email}
      </p>
      <div className="flex flex-wrap gap-2.5 mb-7" data-testid="email-preferences-tags">
        {(state.profile?.available_tags || []).map((t) => {
          const on = tags.includes(t);
          return (
            <button
              key={t}
              type="button"
              onClick={() => setTags((v) => (on ? v.filter((x) => x !== t) : [...v, t]))}
              data-testid={`email-pref-tag-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`px-4 py-2.5 text-[13px] font-light border transition-colors ${
                on ? "bg-sage border-sage text-white" : "border-line text-[#5A5A5A] hover:border-sage"
              }`}
            >
              {t}
            </button>
          );
        })}
      </div>
      {state.done && (
        <p data-testid="email-preferences-saved" className="text-[13.5px] text-sage-dark mb-4">
          Preferences saved.
        </p>
      )}
      <button
        onClick={savePreferences}
        disabled={busy}
        data-testid="email-preferences-save"
        className="px-7 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase hover:bg-sage-dark transition-colors disabled:opacity-60"
      >
        {busy ? "Saving…" : "Save preferences"}
      </button>
      <p className="text-[13px] font-light text-[#8A867F] mt-6">
        Prefer no email at all?{" "}
        <Link
          to={`/email/unsubscribe?token=${encodeURIComponent(token)}`}
          data-testid="email-preferences-unsubscribe-link"
          className="text-sage hover:text-sage-dark"
        >
          Unsubscribe
        </Link>
        .
      </p>
    </Shell>
  );
}
